import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { istAnlegbar, konditionText, listeAus, listenTitel, type Hersteller } from "@repo/shared";

import { ListenKarten } from "../components/hersteller/ListenKarten";
import { NeuerHerstellerFormular } from "../components/hersteller/NeuerHerstellerFormular";
import { api, errorMessage } from "../lib/api";

/** Woher die Preise kommen – wie bisher in der Liste. */
export function Preisquelle({ quelle }: { quelle: Hersteller["quelle"] }) {
  if (quelle === "ohne") return <span className="muted small">kein Konfigurator</span>;
  return (
    <span className={`badge badge--${quelle === "remote" ? "suspended" : "active"}`}>
      {quelle === "remote" ? "Hersteller-API" : "eigener Rechenkern"}
    </span>
  );
}

export function Kategorien({ liste }: { liste: string[] }) {
  if (liste.length === 0) return <span className="muted">—</span>;
  return (
    <span className="kategorien">
      {liste.map((k) => (
        <span key={k} className="kategorie">
          {k}
        </span>
      ))}
    </span>
  );
}

function kontaktperson(h: Hersteller): string | null {
  const p = h.ansprechpartner[0];
  return p ? [p.vorname, p.nachname].filter(Boolean).join(" ") : null;
}

/** Hersteller in vier Listen – zuerst die Auswahl, dann die Liste nach dem Vorbild des ERP. */
export function HerstellerPage() {
  const [params] = useSearchParams();
  const liste = listeAus(params.get("liste"));
  const [hersteller, setHersteller] = useState<Hersteller[] | null>(null);
  const [suche, setSuche] = useState("");
  const [neu, setNeu] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    api.api.admin.hersteller
      .$get()
      .then(async (res) => {
        if (!res.ok) throw new Error(await errorMessage(res, "Hersteller nicht ladbar"));
        const data = await res.json();
        if ("hersteller" in data) setHersteller(data.hersteller);
      })
      .catch((cause: unknown) => setFehler(cause instanceof Error ? cause.message : "Laden fehlgeschlagen"));
  }, []);

  // Andere Liste: Suche und Formular gehören zur alten.
  useEffect(() => {
    setSuche("");
    setNeu(false);
  }, [liste]);

  const gefiltert = useMemo(() => {
    if (!hersteller || !liste) return [];
    const inListe = hersteller.filter((h) => h.art === liste && h.gelistet);
    const q = suche.trim().toLowerCase();
    if (!q) return inListe;
    return inListe.filter((h) => [h.name, h.kuerzel, h.email, h.schluessel].some((w) => w?.toLowerCase().includes(q)));
  }, [hersteller, liste, suche]);

  if (!liste) {
    return (
      <section>
        <h1>Hersteller</h1>
        <p className="muted">
          Stammdaten der Hersteller – intern in OrderCity, z. B. für Bestellungen bei den Herstellern.
          Mandanten sehen nur die Konfiguratoren, keine Herstellerdaten.
        </p>
        {fehler && <p className="error">{fehler}</p>}
        {hersteller === null ? !fehler && <p className="muted">Lade Hersteller …</p> : <ListenKarten hersteller={hersteller} />}
      </section>
    );
  }

  const mitPreisliste = liste === "naehatelier";

  return (
    <section>
      <p className="breadcrumb">
        <Link to="/hersteller">Hersteller</Link> / {listenTitel(liste)}
      </p>
      <h1>{listenTitel(liste)}</h1>

      {fehler && <p className="error">{fehler}</p>}

      <div className="form">
        <input
          type="search"
          placeholder="Name, Kürzel oder E-Mail"
          value={suche}
          onChange={(e) => setSuche(e.target.value)}
          aria-label="Hersteller suchen"
        />
      </div>

      {istAnlegbar(liste) &&
        (neu ? (
          <NeuerHerstellerFormular art={liste} onAbbrechen={() => setNeu(false)} />
        ) : (
          <div className="actions">
            <button type="button" className="primary" onClick={() => setNeu(true)}>
              Neuer Hersteller
            </button>
          </div>
        ))}

      {hersteller === null ? (
        !fehler && <p className="muted">Lade Hersteller …</p>
      ) : gefiltert.length === 0 ? (
        <p className="muted">Keine Hersteller gefunden.</p>
      ) : (
        <div className="tabelle-rahmen">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Kontaktperson</th>
                <th>E-Mail</th>
                <th>Produktkategorien</th>
                <th className="num">Aufschlag %</th>
                <th>Preisquelle</th>
                {liste === "konfigurator" && <th>Preisart / Rabatt</th>}
                {mitPreisliste && <th>Preisliste</th>}
              </tr>
            </thead>
            <tbody>
              {gefiltert.map((h) => (
                <tr key={h.id}>
                  <td>
                    <Link to={`/hersteller/${h.schluessel}`}>{h.name}</Link>
                    <div className="muted small">
                      {h.kuerzel ? `${h.kuerzel} · ` : ""}
                      <code>{h.schluessel}</code>
                    </div>
                    {h.serverAnbindung && (
                      <div>
                        <span className="badge badge--active">Serverangebunden · {h.serverAnbindung}</span>
                      </div>
                    )}
                  </td>
                  <td>{kontaktperson(h) ?? <span className="muted">—</span>}</td>
                  <td>{h.email ? <a href={`mailto:${h.email}`}>{h.email}</a> : <span className="muted">—</span>}</td>
                  <td>
                    <Kategorien liste={h.kategorien} />
                  </td>
                  <td className="num">{h.aufschlagProzent.toLocaleString("de-DE")}</td>
                  <td>
                    <Preisquelle quelle={h.quelle} />
                  </td>
                  {liste === "konfigurator" && <td>{konditionText(h.preisart, h.rabattProzent, h.rabattDynamisch, h.teuerungProzent)}</td>}
                  {mitPreisliste && <td>{h.atelierpreise ? "gepflegt" : <span className="muted">fehlt</span>}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
